import React, { useEffect, useRef, useState, useLayoutEffect } from "react";
import { useSelector } from "react-redux";
import InfiniteScroll from "react-infinite-scroll-component";
import OutsideClickHandler from "react-outside-click-handler";
import MessageHelper from "../../../../entities/message/message-helper";
import { EventEmitter } from "../../../../shared/utils/event-emitter";
import Events from "../../../../const/events";
import { Tooltip } from "react-tooltip";
import "./thread-chat-modal.scss";

const ThreadChatModal = () => {
  const [hoveredSysMsgId, setHoveredSysMsgId] = useState(null);
  const [isVisible, setIsVisible] = useState(false);
  const selectedThread = useSelector(
    (state) => state?.threadStore?.selectedThread || null
  );
  const isLoaderActive = useSelector(
    (state) => state?.messageStore?.isLoaderActive || false
  );
  const messages = useSelector((state) => state?.messageStore?.messages || []);
  const pagination = useSelector(
    (state) => state?.messageStore?.pagination || {}
  );

  const chatBodyRef = useRef(null);
  const modalRef = useRef(null);
  const isLoadingMoreRef = useRef(false);
  const topVisibleMessageIdRef = useRef(null); // Ref to store the ID of the top visible message
  const prevMessagesLengthRef = useRef(messages.length); // Ref to track previous message count

  // Determine if there are more messages to load
  const hasMoreMessages = pagination.page < pagination.totalPages;

  // Find the topmost visible message element
  const findTopVisibleMessageId = () => {
    if (!chatBodyRef.current) return null;
    const container = chatBodyRef.current;
    const containerScrollTop = container.scrollTop;
    const messageElements = container.querySelectorAll('.message-wrapper');

    // Since messages are reversed (newest first in DOM order, visually at bottom),
    // we iterate backwards through the DOM elements to find the first one
    // whose bottom edge is below or at the container's top scroll position.
    // This corresponds to the visually topmost message.
    for (let i = messageElements.length - 1; i >= 0; i--) {
      const element = messageElements[i];
      const elementTop = element.offsetTop;
      const elementBottom = elementTop + element.offsetHeight;

      // Check if the element is at least partially visible at the top
      if (elementBottom > containerScrollTop) {
        return element.id.replace('msg-', ''); // Return the message ID
      }
    }

    // Fallback: If no element meets the condition (e.g., scrolled way down),
    // anchor to the actual first message in the data array (visually topmost).
    if (messages.length > 0) {
        return messages[0]._id;
    }

    return null; // No visible message found
  };


  // Load older messages
  const loadOlderMessages = () => {
    if (hasMoreMessages && chatBodyRef.current && !isLoadingMoreRef.current) {
      isLoadingMoreRef.current = true;

      // Store the ID of the current top visible message before loading
      topVisibleMessageIdRef.current = findTopVisibleMessageId();
      console.log("Anchoring to message ID:", topVisibleMessageIdRef.current);

      // Load more messages
      MessageHelper.loadNextPage().finally(() => {
        isLoadingMoreRef.current = false;
      });
    }
  };

  // Effect to restore scroll position after loading older messages
  // Use useLayoutEffect to run synchronously after DOM updates but before paint
  useLayoutEffect(() => {
    if (topVisibleMessageIdRef.current && chatBodyRef.current) {
      const messageIdToFind = `msg-${topVisibleMessageIdRef.current}`;
      const messageElement = chatBodyRef.current.querySelector(`#${messageIdToFind}`);
      console.log("Trying to scroll to element:", messageElement);

      if (messageElement) {
        messageElement.scrollIntoView({ block: 'start', behavior: 'auto' });
        console.log("Scrolled to:", messageIdToFind);
        // Reset the ref after scrolling
        topVisibleMessageIdRef.current = null;
      } else {
         console.log("Anchor element not found:", messageIdToFind);
        // If element not found, clear the ref anyway
        topVisibleMessageIdRef.current = null;
      }
    }
    // Update previous message length after potential scroll adjustment
    prevMessagesLengthRef.current = messages.length;

  }, [messages]); // Trigger when messages array changes

  // Scroll to the bottom
  const _scrollToBottom = (force = false) => {
    // Only scroll to bottom if forced, or if not anchored
    if (chatBodyRef.current && (force || !topVisibleMessageIdRef.current)) {
       console.log(`Scrolling to bottom. Forced: ${force}, Anchor: ${topVisibleMessageIdRef.current}`);
      // Use timeout to allow potential layout updates after other effects
      setTimeout(() => {
        if (chatBodyRef.current) {
            chatBodyRef.current.scrollTop = chatBodyRef.current.scrollHeight;
        }
      }, 0);
    }
  };

  // Register event listeners
  useEffect(() => {
    const _registerEvents = () => {
      EventEmitter.subscribe(Events.SHOW_THREAD_CHAT_MODAL, () => {
        setIsVisible(true);
        topVisibleMessageIdRef.current = null; // Reset anchor on open
        prevMessagesLengthRef.current = 0; // Reset message count on open
        // Allow time for modal to render and initial messages to load
        setTimeout(() => _scrollToBottom(true), 100); // Force scroll on open
      });
      EventEmitter.subscribe(Events.HIDE_THREAD_CHAT_MODAL, () => {
        setIsVisible(false);
      });
    };

    const _unregisterEvents = () => {
      EventEmitter.cancelAll(Events.SHOW_THREAD_CHAT_MODAL);
      EventEmitter.cancelAll(Events.HIDE_THREAD_CHAT_MODAL);
    };

    _registerEvents();
    return () => {
      _unregisterEvents();
    };
  }, []);

  // Effect to scroll down only for new messages (not when loading older ones)
  useEffect(() => {
    const isNewMessage = messages.length > prevMessagesLengthRef.current && !isLoadingMoreRef.current && !topVisibleMessageIdRef.current;
    console.log(`Checking for new message scroll: isVisible=${isVisible}, isNewMessage=${isNewMessage}, isLoading=${isLoadingMoreRef.current}, anchor=${topVisibleMessageIdRef.current}, prevLen=${prevMessagesLengthRef.current}, currLen=${messages.length}`);

    if (isVisible && isNewMessage) {
       // If modal is visible, we are not loading more, not anchored,
       // and the message count increased, assume it's a new message at the bottom.
      console.log("Detected new message, scrolling to bottom.");
      _scrollToBottom();
    }

    // Update prev length ref here as well, after the check
    // Although useLayoutEffect also does this, redundancy might be safer depending on effect order.
    prevMessagesLengthRef.current = messages.length;

  }, [messages, isVisible]); // Depend on messages and visibility

  // Format timestamp
  const formatTime = (timestamp) => {
    const date = new Date(timestamp);
    return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  // Format date
  const formatDate = (timestamp) => {
    const date = new Date(timestamp);
    return date.toLocaleDateString([], { 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric' 
    });
  };

  // Check if dates are the same day
  const isSameDay = (date1, date2) => {
    const d1 = new Date(date1);
    const d2 = new Date(date2);
    return d1.getDate() === d2.getDate() && 
           d1.getMonth() === d2.getMonth() && 
           d1.getFullYear() === d2.getFullYear();
  };

  // Should show date separator
  const shouldShowDate = (message, previousMessage) => {
    if (!previousMessage) return true;
    return !isSameDay(message.createdAt, previousMessage.createdAt);
  };

  // Function to get initials from name
  const getInitials = (name) => {
    if (!name || typeof name !== 'string') return '?';
    return name
      .split(' ')
      .map(part => part[0])
      .join('')
      .toUpperCase()
      .substring(0, 2);
  };

  // Check if messages should be grouped
  const shouldGroupMessage = (currentMessage, previousMessage) => {
    if (!previousMessage) return false;
    
    return (
      previousMessage?.msgType === 'messages' && 
      currentMessage?.msgType === 'messages' && 
      previousMessage?._by?._id && 
      currentMessage?._by?._id && 
      (previousMessage?._by?._id === currentMessage?._by?._id)
    );
  };

  // Get other participant name for system messages
  const getOtherParticipantName = (contactObj, messageText) => {
    const participants = selectedThread?._contacts;
    if (!participants || !participants.length) return "Unknown";
    
    const participantNames = participants.map((participant) => participant.name);
    let otherParticipantName = participantNames.filter(
      (name) => !messageText.includes(name)
    );
    
    if (otherParticipantName.length === 0) {
      otherParticipantName = participantNames.sort(
        (a, b) => messageText.indexOf(a) - messageText.indexOf(b)
      )[0];
    }
    
    return otherParticipantName;
  };

  // Determine system message type
  const getSystemMessageType = (messageText) => {
    if (messageText.includes("joined the room")) {
      return "success";
    } else if (messageText.includes("left the room")) {
      return "danger";
    }
    return "";
  };

  if (!isVisible || !selectedThread) {
    return null;
  }

  // Get contact names for header
  let modalTitle = "";
  if (selectedThread) {
    if (selectedThread.title?.length) {
      modalTitle = selectedThread.title;
    } else if (selectedThread._contacts?.length) {
      if (selectedThread.type === "room" && selectedThread._contacts.length === 2) {
        modalTitle = `${selectedThread._contacts[0]?.name || 'Unknown'} & ${selectedThread._contacts[1]?.name || 'Unknown'}`;
      } else {
        modalTitle = selectedThread._contacts[0]?.name || selectedThread._contacts[0]?.phone || "Unknown Contact";
      }
    }
  }

  return (
    <div className="thread-chat-modal">
      <div className="modal-backdrop" onClick={() => EventEmitter.dispatch(Events.HIDE_THREAD_CHAT_MODAL)}></div>
      <OutsideClickHandler onOutsideClick={() => EventEmitter.dispatch(Events.HIDE_THREAD_CHAT_MODAL)}>
        <div className="modal-content" ref={modalRef}>
          {/* Modal Header */}
          <div className="modal-header">
            <h5 className="modal-title">Chat with {modalTitle}</h5>
            <button 
              type="button" 
              className="close-button"
              onClick={() => EventEmitter.dispatch(Events.HIDE_THREAD_CHAT_MODAL)}
            >
              <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M18 6L6 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M6 6L18 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </button>
          </div>

          {/* Modal Body - Chat Messages */}
          <div className="modal-body">
            <div 
              className="chat-container" 
              id="scrollableChatDiv" 
              ref={chatBodyRef}
            >
              <InfiniteScroll
                dataLength={messages.length}
                next={loadOlderMessages}
                hasMore={hasMoreMessages}
                style={{
                  display: "flex",
                  flexDirection: "column-reverse",
                  overflow: "visible",
                }}
                scrollableTarget="scrollableChatDiv"
                inverse={true}
                loader={
                  <div className="loader-wrapper">
                    <img
                      src="assets/images/ring-loader.svg"
                      alt="loader"
                      className="loader"
                    />
                  </div>
                }
              >
                {messages.length === 0 ? (
                  <div className="no-messages">
                    <p>No messages in this conversation yet.</p>
                  </div>
                ) : (
                  messages.map((message, messageIndex) => {
                    const previousMessage = messages[messageIndex + 1];
                    const showDateSeparator = shouldShowDate(message, previousMessage);
                    const isGrouped = shouldGroupMessage(message, previousMessage);
                    
                    // Special handling for system messages
                    if (message.msgType !== "messages") {
                      const systemMessageType = getSystemMessageType(message.message);
                      
                      if (selectedThread?.type === "room") {
                        const otherParticipant = getOtherParticipantName(message._by, message.message);
                        return (
                          // Add unique ID to system messages as well
                          <React.Fragment key={message._id}>
                            {showDateSeparator && (
                              <div className="date-separator">
                                <span>{formatDate(message.createdAt)}</span>
                              </div>
                            )}
                            <div className="middleNotification" id={`msg-${message._id}`}>
                              <span className={systemMessageType}>
                                <span
                                  className="system-message-hover"
                                  style={{ textDecoration: 'underline', cursor: 'pointer', position: 'relative' }}
                                  onMouseEnter={() => setHoveredSysMsgId(message._id)}
                                  onMouseLeave={() => setHoveredSysMsgId(null)}
                                >
                                  System message sent to {otherParticipant}
                                  {hoveredSysMsgId === message._id && (
                                    <span
                                      style={{
                                        position: 'absolute',
                                        left: 0,
                                        top: '100%',
                                        background: '#222',
                                        color: '#fff',
                                        padding: '8px 12px',
                                        borderRadius: 4,
                                        whiteSpace: 'pre-wrap',
                                        zIndex: 100,
                                        minWidth: 200,
                                        marginTop: 4,
                                        boxShadow: '0 2px 8px rgba(0,0,0,0.15)'
                                      }}
                                    >
                                      {message.message}
                                    </span>
                                  )}
                                </span>
                                <span className="timestamp">
                                  {formatTime(message.createdAt)}
                                </span>
                              </span>
                            </div>
                          </React.Fragment>
                        );                      } else {
                        return (
                          // Add unique ID to system messages as well
                          <React.Fragment key={message._id}>
                            {showDateSeparator && (
                              <div className="date-separator">
                                <span>{formatDate(message.createdAt)}</span>
                              </div>
                            )}
                            <div className="system-message message-wrapper" id={`msg-${message._id}`}>
                              <p>
                                {message.message}
                                <span className="timestamp">
                                  {formatTime(message.createdAt)}
                                </span>
                              </p>
                            </div>
                          </React.Fragment>
                        );
                      }
                    }
                    
                    // Regular messages
                    const isIncoming = selectedThread.type === "room" || message.direction === "incoming";
                    const senderName = message._by?.name || "Unknown";
                    
                    return (
                      <React.Fragment key={message._id}>
                        {showDateSeparator && (
                          <div className="date-separator">
                            <span>{formatDate(message.createdAt)}</span>
                          </div>
                        )}
                        {/* <div 
                          className={`message-wrapper ${isIncoming ? 'incoming' : 'outgoing'}`}
                          id={`msg-${message._id}`}
                        >
                          {isIncoming && !isGrouped && selectedThread.type === "room" && (
                            <div className="avatar">
                              <span>{getInitials(senderName)}</span>
                            </div>
                          )}
                          <div className="message">
                            {selectedThread.type === "room" && !isGrouped && (
                              <div className="sender-name">
                                {senderName}
                              </div>
                            )}
                            <div className={`message-content ${message?.metadata?.ai_filtered ? 'ai-flagged' : ''}`}>
                              {message.message}
                              {message?.metadata?.ai_filtered && (
                                <span className="ai-flag-indicator" data-tooltip-id="ai-flag-tooltip">
                                  <i className="fa fa-shield-alt"></i>
                                </span>
                              )}
                            </div>
                            <div className="message-time">
                              {formatTime(message.createdAt)}
                              {!isIncoming && (
                                <span className="status">
                                  {message.status === "read" ? (
                                    <span className="read" title="Read">
                                      <i className="fa fa-check-double"></i>
                                    </span>
                                  ) : (
                                    <span className="notRead" title="Delivered">
                                      <i className="fa fa-check"></i>
                                    </span>
                                  )}
                                </span>
                              )}
                            </div>
                          </div>
                        </div> */}


                        <div
                          className={`media message-wrapper ${isIncoming ? 'orange' : 'yellow'}`}
                          id={`msg-${message._id}`}
                        >
                          <div className="media-img">
                              {isIncoming && !isGrouped && selectedThread.type === "room" && (
                                <div className="avatar-box thumb-sm align-self-center"><span className="thumb-sm justify-content-center d-flex align-items-center bg-soft-primary rounded-circle">{getInitials(senderName)}</span></div>
                              )}
                              {selectedThread.type === "room" && !isGrouped && (
                                <div className="fullName">
                                  {senderName}
                                </div>
                              )}
                          </div>
                          <div className="media-body">
                              <div className={`chat-msg ${message?.metadata?.ai_filtered ? 'ai-flagged' : ''}`}>
                                <p>
                                  {message.message}
                                  {message?.metadata?.ai_filtered && (
                                    <span className="ai-flag-indicator" data-tooltip-id="ai-flag-tooltip">
                                      <i className="fa fa-shield-alt"></i>
                                    </span>
                                  )}
                                  <span class="timestamp">Friday at 06:11 PM<span class="status"><span class="read" title="Read"><i class="fa fa-check-double"></i></span></span></span>
                                </p>
                              </div>
                          </div>
                        </div>
                      </React.Fragment>
                    );
                  })
                )}
              </InfiniteScroll>
              {/* Add tooltip for AI filtered messages */}
              <Tooltip id="ai-flag-tooltip" place="bottom">
                This message was filtered by AI and not forwarded as it violates organization policies
              </Tooltip>
            </div>
          </div>
        </div>
      </OutsideClickHandler>
    </div>
  );
};

export default ThreadChatModal; 